import { z } from "zod";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import { ItemProps, YesNo } from "@/interfaces/employee.interface";
import { EmploymentType } from "@/interfaces/globals";
import {
  AddressValidatorRegex,
  addressValidator,
  basicValidator,
  createFileUploadValidator,
  dateValidator,
  emailValidator,
  fileOrStringArrayValidator,
  imageValidator,
  phoneNumberValidator,
  reactSelectValidator,
} from "@/lib/validators";
import { DeactivatedHistoryProps } from "@/pages/workforce/employees/components/Termination/DeactivatedHistoryTable";

export enum EmployeeRole {
  GENERAL_LABOUR = "GENERAL_LABOUR",
  DRIVER = "DRIVER",
}

export const AddEmployee = z.object({
  firstName: basicValidator(true, "First Name is Required.", 2, 50),
  middleName: basicValidator(false, "Middle Name invalid.").default(""),
  lastName: basicValidator(true, "Last Name is Required.", 2, 50),
  email: emailValidator(true),
  phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
  address: addressValidator(true, ERROR_MSG.REQUIRED_ADDRESS),
  whiteCardNo: basicValidator(true, "White Card Number Is Required."),
  describesYou: basicValidator(
    true,
    "What Describes You is Required.",
    2,
    50,
    true,
  ),
  // for request
  employmentType: z.string().optional(),
  status: z.string().optional(),
});

const updateEmployeeSchema = AddEmployee.extend({
  deviceId: z.string().optional(),
  tabAccess: z.boolean().optional(),
});

const trainingSchema = z
  .object({
    name: basicValidator(true),
    issuedDate: dateValidator(true, "Issue date is required"),
    ticketUrl: z.string().optional(),
    expiryDate: dateValidator(false),
    image: createFileUploadValidator({
      required: true,
    }),
  })
  .superRefine((data, ctx) => {
    if (data.name && !data.issuedDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["issuedDate"],
      });
    }

    if (data.name && Array.isArray(data.image) && !data.image.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["image"],
      });
    }
  });

export const employeeWorkInfoSchema = z.object({
  employmentSeeking: z.object({
    employmentType: z.nativeEnum(EmploymentType, {
      message: "Employment Type is required.",
    }),
    desiredRole: z.nativeEnum(EmployeeRole, {
      message: "Desired Role is required.",
    }),
    proposedStartDate: dateValidator(true, ERROR_MSG.DUE_DATE_REQ),
  }),
  previousExperience: z.object({
    trainings: z
      .array(z.object({ label: z.string(), value: z.string() }), {
        required_error: "Please Select Trainings/Tickets.",
      })
      .optional(),
    referenceDetails: z.string().optional(),
    skills: reactSelectValidator(ERROR_MSG.REQUIRED_SKILLS, false),
  }),
  trainings: z.array(trainingSchema),
});

export const employeeWorkInfoSchemaResponse = employeeWorkInfoSchema
  .omit({
    previousExperience: true,
    trainings: true,
  })
  .extend({
    previousExperience: z.object({
      trainings: z.array(trainingSchema),
      referenceDetails: z.string().optional(),
      skills: reactSelectValidator(ERROR_MSG.REQUIRED_SKILLS, false),
    }),
  });

export const employeeImportantInfoSchema = z.object({
  emergencyContact: z.object({
    fullName: basicValidator(true),
    phoneNumber: phoneNumberValidator(false, "Invalid Phone Number"),
    relationship: basicValidator(true),
    address: addressValidator(true),
  }),

  bankDetails: z.object({
    primary: z.object({
      bankName: basicValidator(true),
      bsb: basicValidator(true),
      accountNumber: basicValidator(true),
    }),
    secondary: z
      .object({
        bsb: z.string().optional(),
        bankName: z.string().optional(),
        accountNumber: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        const { bsb, bankName, accountNumber } = data;

        if (bsb && (!bankName || !accountNumber)) {
          if (!bankName) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Required",
              path: ["bankName"],
            });
          }
          if (!accountNumber) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Required",
              path: ["accountNumber"],
            });
          }
        }

        if ((bankName || accountNumber) && !bsb) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["bsb"],
          });
        }
      })
      .optional(),
  }),
  superAnnuationDetails: z.object({
    bankName: basicValidator(true),
    membershipNumber: basicValidator(true),
  }),
  documentChecklist: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, {
      message: "Please upload at least one document",
    }),
  files: z.array(
    z
      .object({
        label: z.string(),
        value: fileOrStringArrayValidator,
      })
      .superRefine((data, ctx) => {
        if (data.label && !data.value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["value"],
          });
        }
      }),
  ),
});

export const employeeImportantInfoResponse = employeeImportantInfoSchema
  .omit({
    documentChecklist: true,
    files: true,
  })
  .merge(
    z.object({
      documentChecklist: z.array(
        z.object({
          url: z.string(),
          name: z.string(),
        }),
      ),
    }),
  );

export const declarationSchema = z.object({
  healthDetails: z
    .object({
      medicalFitness: z.enum(["YES", "NO"]),
      medicalDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.medicalFitness === "YES" && !data?.medicalDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["medicalDetails"],
        });
      }
    }),
  covidVaccinated: z.enum(["YES", "NO"]),
  workerCompensation: z
    .object({
      workerCompensation: z.enum(["YES", "NO"]),
      compensationDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.workerCompensation === "YES" && !data?.compensationDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["compensationDetails"],
        });
      }
    }),
  criminalConviction: z
    .object({
      pendingCharges: z.enum(["YES", "NO"]),
      chargeDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.pendingCharges === "YES" && !data?.chargeDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["chargeDetails"],
        });
      }
    }),
  signature: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Signature is required",
        path: [],
      });
    }
  }),
  passportNumber: basicValidator(true, "passport number is required."),
  driversLicenseNo: basicValidator(true, "driving license number is required."),
  // whiteCardNo: basicValidator(true, "white card no number is required."),

  // for frontend only
  privacyCheck: z.boolean().optional(),
});

const employeeWorkSchema = z.object({
  emergencyDetails: z.object({
    name: basicValidator(true),
    phoneNumber: phoneNumberValidator(false, "Invalid Phone Number"),
    relationship: basicValidator(true),
    address: addressValidator(true),
  }),
  documents: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .min(1, {
      message: "Please upload at least one document",
    }),
  files: z.array(
    z
      .object({
        label: z.string(),
        value: z.any({
          required_error: "Please upload at least one document",
        }),
      })
      .superRefine((data, ctx) => {
        if (data.label && !data.value) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["value"],
          });
        }
      }),
  ),
  bankDetails: z.object({
    primary: z.object({
      bankName: basicValidator(true),
      bsb: basicValidator(true),
      accountNumber: basicValidator(true),
    }),
    secondary: z
      .object({
        bsb: z.string().optional(),
        bankName: z.string().optional(),
        accountNumber: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        const { bsb, bankName, accountNumber } = data;

        if (bsb && (!bankName || !accountNumber)) {
          if (!bankName) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Required",
              path: ["bankName"],
            });
          }
          if (!accountNumber) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Required",
              path: ["accountNumber"],
            });
          }
        }

        if ((bankName || accountNumber) && !bsb) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["bsb"],
          });
        }
      })
      .optional(),
  }),
  superAnnuationDetails: z.object({
    bankName: basicValidator(true),
    membershipNumber: basicValidator(true),
  }),
});

const employeeDeclarationSchema = z.object({
  employmentSeeking: z.object({
    desiredRole: basicValidator(true, "Desired role is required"),
    proposedStartDate: dateValidator(true, "Proposed start date is required"),
    // transportMethod: basicValidator(true),
  }),
  employmentType: z.object({
    employmentType: basicValidator(true, "Employment type is required"),
    // desiredRole: basicValidator(true),
    describesYou: basicValidator(true),
    taxFileNumber: basicValidator(true),
    passportNumber: basicValidator(true),
    driversLicenseNo: basicValidator(true),
    whiteCardNo: basicValidator(true),
  }),
  previousExperience: z.object({
    textField: z.string().optional(),
    // skills: z.array(z.object({ label: z.string(), value: z.string() })),
    skills: reactSelectValidator(ERROR_MSG.REQUIRED_SKILLS),
    trainings: z.array(z.object({ label: z.string(), value: z.string() })),
  }),
  trainings: z.array(
    z
      .object({
        name: basicValidator(true),
        ticketUrl: z.string().optional(),
        issuedDate: dateValidator(true, "issue date is required"),
        expiryDate: dateValidator(false),
        image: imageValidator(2, false, ERROR_MSG.REQUIRED_IMAGE),
      })
      .superRefine((data, ctx) => {
        if (data.name && !data.issuedDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["issuedDate"],
          });
        }
        if (data.name && Array.isArray(data.image) && !data.image.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["image"],
          });
        }
      }),
  ),
  referenceDetails: z
    .object({
      isAvailable: z.enum(["YES", "NO"]),
      referenceDetails: z.string().trim().optional(),
    })
    .refine(
      (data) => {
        if (data.isAvailable === YesNo.Yes && !data.referenceDetails) {
          return false;
        }

        return true;
      },
      {
        message: "Reference Details is required",
        path: ["referenceDetails"],
      },
    ),
});

const employeeFinalReviewSchema = z.object({
  healthDetails: z
    .object({
      medicalFitness: z.enum(["YES", "NO"]),
      medicalDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.medicalFitness === "YES" && !data?.medicalDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["medicalDetails"],
        });
      }
    }),
  covidVaccinated: z.enum(["YES", "NO"]),
  workerCompensation: z
    .object({
      workerComponenstation: z.enum(["YES", "NO"]),
      compensationDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.workerComponenstation === "YES" && !data?.compensationDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["compensationDetails"],
        });
      }
    }),
  criminalConviction: z
    .object({
      pendingCharges: z.enum(["YES", "NO"]),
      chargeDetails: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data?.pendingCharges === "YES" && !data?.chargeDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "This field is required",
          path: ["chargeDetails"],
        });
      }
    }),
  signature: z.string().superRefine((data, ctx) => {
    if (!data) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: [],
      });
    }
  }),
  privacyCheck: z.boolean().optional(),
});

export const employeeTermination = z.object({
  otherReason: z.string().optional(),
  terminationDate: dateValidator(true, "Termination Date is required"),
  terminationReason: z.string().min(1, { message: "Required" }),
  employeeEvaluation: z
    .object({
      attendance: z.string(),
      cooperation: z.string(),
      initiative: z.string(),
      jobKnowledge: z.string(),
      workQuality: z.string(),
    })
    .superRefine((data, ctx) => {
      if (
        !data?.attendance ||
        !data?.cooperation ||
        !data?.initiative ||
        !data?.jobKnowledge ||
        !data?.workQuality
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please Select All",
          path: [],
        });
      }
    }),
  action: z.string().optional(),
  rehireRecommendation: z
    .string()
    .min(1, { message: "Please Select Rehire Recommendation" }),
  employeeNotice: z.object({
    givenStatus: z.string().min(1, { message: "Please Select One" }).max(3),
    days: z.coerce.number().min(1, { message: "Please Select Days" }),
    date: dateValidator(true, "Date is required"),
  }),
  severancePay: z.string().min(1, { message: "Please Select One" }).max(3),
  additionalComment: z.string().optional(),
  actor: z
    .object({
      firstName: z.string(),
      middleName: z.string().optional(),
      lastName: z.string(),
      email: z.string().email(),
      phoneNumber: phoneNumberValidator(false, "Invalid Phone Number"),
    })
    .optional(),
});

export const UpdateEmployeeBasicInfo = z.object({
  firstName: z
    .string({
      required_error: "First Name is required.",
    })
    .min(2, {
      message: "First Name is required.",
    })
    .max(50, {
      message: "First Name is too long.",
    }),
  middleName: z
    .string({
      required_error: "Middle Name is required.",
    })
    .max(50, {
      message: "Middle Name is too long.",
    })
    .optional(),
  lastName: z
    .string({
      required_error: "Last Name is required.",
    })
    .min(2, {
      message: "Last Name is required.",
    })
    .max(50, {
      message: "Last Name is too long.",
    }),
  phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
  email: z.string().email(),
  address: z
    .string({
      required_error: "Address is required.",
    })
    .regex(AddressValidatorRegex, {
      message: "Invalid address format",
    })
    .min(2, {
      message: "Address is required.",
    })
    .max(50, {
      message: "Address is too long.",
    }),
  employmentType: z
    .string({
      required_error: "Employment Type is required.",
    })
    .min(2, {
      message: "Employment Type is required.",
    })
    .max(50, {
      message: "Employment Type is too long.",
    }),
  bankDetails: z.object({
    bsb: z.string(),
    bankName: z.string(),
    accountNumber: z.string(),
  }),
  secondaryBankDetails: z.object({
    bsb: z.string().optional(),
    bankName: z.string().optional(),
    accountNumber: z.string().optional(),
  }),
  emergencyDetails: z.object({
    name: z.string(),
    address: z.string().regex(AddressValidatorRegex, {
      message: "Invalid address format",
    }),
    phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
    relationship: z.string(),
  }),
  superAnnuationDetails: z.object({
    bankName: z.string(),
    membershipNumber: z.string(),
  }),
});

export const UpdateEmployeeWorkInfo = z.object({
  trainings: z.array(z.string()),
  employmentType: z.object({
    desiredRole: z.string(),
    whiteCardNo: z.string(),
    describesYou: z.string(),
    taxFileNumber: z.string(),
    passportNumber: z.string(),
    driversLicenseNo: z.string(),
  }),
  referenceDetails: z.string().optional(),
  employmentSeeking: z.object({
    desiredRole: z.string(),
    transportMethod: z.string(),
    proposedStartDate: z.string(),
  }),
  previousExperience: z.object({
    skills: z.array(z.string()),
    textField: z.string(),
  }),
});

export const UpdateEmployeeDeclaration = z.object({
  signature: z.string(),
  healthDetails: z.object({
    medicalDetails: z.string(),
    medicalFitness: z.string(),
  }),
  covidVaccinated: z.string(),
  criminalConvictions: z.object({
    chargeDetails: z.string(),
    pendingCharges: z.string(),
  }),
  workerCompensation: z.object({
    compensationDetails: z.string(),
    workerCompenstation: z.string(),
  }),
});

export const UpdateEmployee = [
  UpdateEmployeeBasicInfo,
  UpdateEmployeeWorkInfo,
  UpdateEmployeeDeclaration,
];

export {
  employeeDeclarationSchema,
  employeeFinalReviewSchema,
  employeeWorkSchema,
  updateEmployeeSchema,
};

export type TAddEmployeeForm = z.infer<typeof AddEmployee>;
export type TUpdateEmployee = z.infer<typeof updateEmployeeSchema>;
export type TemployeeWorkInfoSchema = z.infer<typeof employeeWorkInfoSchema>;
export type TemployeeImportantInfoSchema = z.infer<
  typeof employeeImportantInfoSchema
>;
export type TdeclarationSchema = z.infer<typeof declarationSchema>;
export type TemployeeImportantInfoResponse = z.infer<
  typeof employeeImportantInfoResponse
>;

export type TEmployeeTermination = z.infer<typeof employeeTermination>;

export type TEmployeeWork = z.infer<typeof employeeWorkSchema>;
interface Document {
  url: string;
  name: string;
}
export type TEmployeeWorkResponse = Omit<TEmployeeWork, "documents"> & {
  documents: Document[];
};

export type TEmployeeDeclaration = z.infer<typeof employeeDeclarationSchema>;

export type TEmployeeFinalReview = z.infer<typeof employeeFinalReviewSchema>;

export type T_EmployeeWorkInfoSchemaResponse = z.infer<
  typeof employeeWorkInfoSchemaResponse
>;

export type TGetSingleEmployee = {
  id: string;
  workspaceId: string;
  userId: string;
  onboardingStatus: string;
  approvedStatus: string;
  whiteCardNo: string;
  describesYou: string;
  basicInfo: TEmployeeWorkResponse;
  declaration: TEmployeeFinalReview;
  // workInfo: TEmployeeDeclaration;
  workInfo: T_EmployeeWorkInfoSchemaResponse;
  user: TUpdateEmployee & { status: string };
  address?: string;
  employeeStatusRecords:
    | TEmployeeTermination[]
    | DeactivatedHistoryProps[]
    | ItemProps[];
  image?: string;

  // for req
  employeeRole: string;
};

export const verifyOtpSchema = z.object({
  otp: z
    .string()
    .min(6, { message: "OTP is required" })
    .max(6, { message: "OTP is required" }),
});
export type TverifyOtpSchema = z.infer<typeof verifyOtpSchema>;
